import Loader from "@components/base/loader/Loader";
import { captureException } from "@sentry/react";
import { getApiUrl } from "@utils/baseUrl";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ContextData } from "../../../containers/shipments/shipments";
import { analyticsTypeDictionary } from "../../../core/global/global-country-dictionary";
import GlobalAnalytics from "../../../shared/analytics-components/globalAnalytics";
import { analyticDropdownOptions, defaultData as multiCountryDefaultData } from "../../intel/default-values/multi-countries";
import { ERROR_MSG } from "../../../utils/enum";
import { getIntelUrl } from "../../../utils/intelUrl";
import { generateQueryString } from "../../../core/utility/utility";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";

const buildAnalyticsPayload = ({ searchedState, filters, analysisKey }) => {
  const searchValues = searchedState?.search?.map((item) => item?.label).filter(Boolean) || [];
  const searchCategory = searchedState?.search?.[0]?.category;
  const searchKey = searchCategory === "HS Code" ? "product_hscode" : searchCategory === "Product" ? "product_description" : "product_and_hscode";
  const payload = {
    from: 0,
    sort: "alias_shipment_date",
    search_type: "basic",
    order: "desc",
    companyType: "",
    analyticsField: true,
    reload: false,
    is_from_alert: "",
    hscode_profile_key: "",
    companyKey: "",
    companyPageNo: 1,
    searchEnable: true,
    isMerged: false,
    isSubsidiaryIncluded: false,
    customType: "shipments",
    redirectingProfile: "",
    redirectingProfilePartner: "",
    is_from_autosuggest: false,
    fromDate: filters?.dateRange?.startDate || "",
    toDate: filters?.dateRange?.endDate || "",
    search: searchValues.join("||"),
    key: searchKey,
    queryType: "contains",
    is_chemical: false,
    isFilterDataIncludes: false,
    industry: "others",
    analysis: analysisKey || "shipments",
  };
  const tradeDirection = searchedState?.tradeDirection?.value;
  const isImport = tradeDirection === "import";
  if (searchedState?.datasetType) payload.data_type = searchedState.datasetType;
  if (filters?.hsCode?.length > 0) payload.sixDigitHsCode = filters.hsCode;
  if (filters?.jurisdiction?.length > 0) {
    if (isImport) {
      payload.inboundCountry = filters.jurisdiction.map((item) => item.toLowerCase());
    } else {
      payload.outboundCountry = filters.jurisdiction.map((item) => item.toLowerCase());
    }
  }
  return payload;
};

const noop = () => null;

const stubContextValue = {
  type: "multicountry",
  setType: noop,
  defaultType: "multicountry",
  setCustomLoader: noop,
  customShipmentGridCostomizeOption: [],
  customShipmentGridSelectedList: [],
  setCustomShipmentGridList: noop,
};

const analyticsHeaderItems = [
  { label: "Total Shipments", key: "total_shipments", valuePrefix: "", valueSuffix: "" },
  { label: "Total Shipments Weight", key: "total_weight", valueSuffix: "kg", valuePrefix: "" },
  { label: "Total Shipments Value", key: "total_value", valueSuffix: "", valuePrefix: "USD" },
];

const analyticsFieldsMapping = [
  { key: "import_trends", filterKey: "", label: "Import Trends", type: analyticsTypeDictionary.trends, showTrends: true, showlevelTwo: false, modalHeader: "Import Trends" },
  { key: "export_trends", filterKey: "", label: "Export Trends", type: analyticsTypeDictionary.trends, showTrends: true, showlevelTwo: false, modalHeader: "Export Trends" },
  { key: "inbound_country", filterKey: "inboundCountryRegion", label: "Top Countries of Import", type: analyticsTypeDictionary.horizontalCountry, showTrends: true, showlevelTwo: true, defaultKey: "port_of_lading", modalHeader: "Inbound Countries" },
  { key: "outbound_country", filterKey: "outboundCountryRegion", label: "Top Countries of Export", type: analyticsTypeDictionary.horizontalCountry, showTrends: true, showlevelTwo: true, defaultKey: "port_of_unlading", modalHeader: "Outbound Countries" },
  { key: "trade_country_of_export_to_import", filterKey1: "outboundCountryRegion", filterKey2: "inboundCountryRegion", label: "Top Trade Routes (Country of Export → Country of Import)", headerOne: "Country of Export", headerTwo: "Country of Import", filterOne: "filterField1", filterTwo: "filterField2", keyOne: "field1Country", keyTwo: "field2Country", cityOne: "field1", cityTwo: "field2", keyOneCountryCode: "field1CountryCode", keyTwoCountryCode: "field2CountryCode", type: analyticsTypeDictionary.topTwo, showTrends: true, grpType: "multi", showlevelTwo: true, defaultKey: "derived_consignee_master_name" },
];

const defaultWidgetOrder = (analyticsFieldsMapping || []).filter((item) => item && item.key).map((item) => item.key);

const ProductAnalytics = ({ searchedState, filters, currentUserPlan, isActive }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisKey, setAnalysisKey] = useState(multiCountryDefaultData.analyticsDefault || analyticDropdownOptions[2].value);
  const [analyticsDataKey, setAnalyticsDataKey] = useState(0);
  const lastSignatureRef = useRef("");
  const abortRef = useRef(null);

  const finalReqObj = useMemo(() => buildAnalyticsPayload({ searchedState, filters, analysisKey }), [searchedState, filters, analysisKey]);

  const fetchAnalytics = async (key = analysisKey) => {
    if (!searchedState) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const payload = buildAnalyticsPayload({ searchedState, filters, analysisKey: key });
      const { data } = await axios.post(getApiUrl("analytics/intelAnalyticsMultiCountry"), payload, { signal: controller.signal });
      if (!data) {
        setAnalyticsData(null);
        return;
      }
      const enriched = {
        ...data,
        analysis: data.analysis || key,
        widgetOrder: Array.isArray(data.widgetOrder) && data.widgetOrder.length > 0 ? data.widgetOrder : defaultWidgetOrder,
      };
      setAnalyticsData(enriched);
      setAnalyticsDataKey((k) => k + 1);
      trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchAnalyticsFetched, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
    } catch (err) {
      if (err.name === "AbortError" || axios.isCancel(err)) return;
      captureException(err);
      toast.error(ERROR_MSG);
      setAnalyticsData(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    const signature = JSON.stringify({
      search: searchedState?.search?.map((s) => s.label),
      tradeDirection: searchedState?.tradeDirection?.value,
      datasetType: searchedState?.datasetType,
      dateRange: filters?.dateRange?.label,
      jurisdiction: filters?.jurisdiction,
      hsCode: filters?.hsCode,
      analysisKey,
    });
    if (signature === lastSignatureRef.current) return;
    lastSignatureRef.current = signature;
    fetchAnalytics(analysisKey);
  }, [searchedState, filters, analysisKey, isActive]);

  const handleAnalyticsDataSource = (e) => {
    const next = e?.value ?? e;
    if (next && next !== analysisKey) {
      setAnalysisKey(next);
    } else {
      fetchAnalytics(analysisKey);
    }
  };

  const handleFilterRedirect = (redirectData = {}) => {
    const { analyticsViewAllKey, filter_value1, filter_value2 } = redirectData;
    const dashlet = analyticsFieldsMapping.find((item) => item.key === analyticsViewAllKey);
    if (!dashlet) return;

    const filterEventMap = {
      inbound_country: trackingEventDictionary.newMay2026ProductSearchAnalyticsTopCountriesImportClick,
      outbound_country: trackingEventDictionary.newMay2026ProductSearchAnalyticsTopCountriesExportClick,
      trade_country_of_export_to_import: trackingEventDictionary.newMay2026ProductSearchAnalyticsTopTradeRoutesClick,
    };
    const filterEvent = filterEventMap[analyticsViewAllKey];
    if (filterEvent) trademoTrackingEvents(filterEvent, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));

    const searchCategory = searchedState?.search?.[0]?.category;
    const currentKey = searchCategory === "HS Code" ? "product_hscode" : "product_description";

    const queryPayload = {
      country: "multicountry",
      type: searchedState?.tradeDirection?.value || "import",
      toAnalytics: "true",
      searchedItem: (searchedState?.search || [])
        .map((s) => s?.label)
        .filter(Boolean)
        .join("||"),
      currentKey,
      matchType: "contains",
      fromDate: filters?.dateRange?.startDate || "",
      toDate: filters?.dateRange?.endDate || "",
      data_type: searchedState?.datasetType || "",
    };

    if (dashlet.filterKey && filter_value1) {
      queryPayload.analyticsFilterKey = dashlet.filterKey;
      queryPayload.analyticsFilterValue = Array.isArray(filter_value1) ? filter_value1.join(",") : filter_value1;
    } else if (dashlet.filterKey1 && filter_value1) {
      queryPayload.analyticsFilterKey = dashlet.filterKey1;
      queryPayload.analyticsFilterValue = Array.isArray(filter_value1) ? filter_value1.join(",") : filter_value1;
    }
    if (dashlet.filterKey2 && filter_value2) {
      queryPayload.analyticsFilterKey2 = dashlet.filterKey2;
      queryPayload.analyticsFilterValue2 = Array.isArray(filter_value2) ? filter_value2.join(",") : filter_value2;
    }

    const qs = generateQueryString(queryPayload);
    const base = getIntelUrl();
    const slash = base.endsWith("/") ? "" : "/";
    window.open(`${base}${slash}all-country-shipments?${qs}`, "_blank");
  };

  if (!searchedState) return null;

  return (
    <div className="tw-mt-3">
      {loading && <Loader />}
      <ContextData.Provider value={stubContextValue}>
        {analyticsData && Object.keys(analyticsData).length > 0 && analyticsData.total_shipments === 0 && analyticsData.total_value === 0 && analyticsData.total_weight === 0 ? (
          <div className="tw-text-[15px] tw-gap-3 tw-max-w-72 tw-mx-auto tw-mt-12 tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col tw-text-center tw-text-gray-700">No analytics data available for this product</div>
        ) : analyticsData && Object.keys(analyticsData).length > 0 ? (
          <GlobalAnalytics
            key={analyticsDataKey}
            trade_direction={multiCountryDefaultData.manyCountryType || ""}
            country={(multiCountryDefaultData.country_small || multiCountryDefaultData.countryCode || "").toString().toLowerCase()}
            isDownloadPDFExport={multiCountryDefaultData.exportPDFInAnalytics}
            isLoader={loading}
            isDownloadPDFExportFrontend={!!multiCountryDefaultData.exportPDFInAnalyticsFrontend}
            dropdownOptions={analyticDropdownOptions}
            mainLabelOptions={analyticDropdownOptions}
            analyticsType={analyticsTypeDictionary}
            analyticsHeaderItems={analyticsHeaderItems}
            analyticsItems={analyticsFieldsMapping}
            analyticsViewAllData={handleFilterRedirect}
            fetchSummaryTrendsData={noop}
            analyticsDataSource={handleAnalyticsDataSource}
            graphData={analyticsData}
            downloadAnalyticsPDF={false}
            downloadPDF={noop}
            from={multiCountryDefaultData.analyticsCountryID}
            entireRequestObject={finalReqObj}
            filterList={[]}
            appliedState={{}}
            showTwoLevel={multiCountryDefaultData.showTwoLevel}
            pathname={window.location.pathname}
            analyticsCountryID={multiCountryDefaultData.analyticsCountryID}
            currentUserPlan={currentUserPlan}
            includeFilter={[]}
            excludeFilter={[]}
            currentTab={"analytics"}
            embedReadOnlyAnalytics
            disableAnalyticsHeaderSelection
            enableFilterRedirect
          />
        ) : !loading ? (
          <div className="tw-text-[15px] tw-gap-3 tw-max-w-72 tw-mx-auto tw-mt-12 tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col tw-text-center tw-text-gray-700">No analytics data available for this search.</div>
        ) : null}
      </ContextData.Provider>
    </div>
  );
};

export default ProductAnalytics;
